/**
 * Maps one authorization body onto an Auth Letter success card.
 *
 * @module mapAuthorizationWritebacksToCards
 */
import {
  AuthorizationCardInfoProps,
  ServiceCardInfoCardProps,
} from "../../../data-model/authLetterSuccessModel";
import {
  CandidAuthorizationPayload,
  CandidWritebackRecord,
} from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { formatDate } from "../../../utils/formatDate";
import {
  capitalizeString,
  isValidString,
} from "../../../utils/stringModifications";

/** Display format for card timestamps. */
const CARD_DATETIME_FORMAT = "MM/DD/YYYY HH:mm:ss";

/** Display format for authorization period dates. */
const CARD_DATE_FORMAT = "MM/DD/YYYY";

/** Tag colors for a successful write-back. */
const SUCCESS_TAG_COLORS = {
  textColor: "#005D49",
  bgColor: "#E6F3F0",
};

/** Tag colors for a failed write-back. */
const ERROR_TAG_COLORS = {
  textColor: "#CC0300",
  bgColor: "#FFE8E8",
};

/**
 * Formats `period_start` and `period_end` as a single date-range string.
 *
 * @param payload - One authorization body
 * @returns `MM/DD/YYYY - MM/DD/YYYY`, or an empty string
 */
const formatPeriodRange = (payload: CandidAuthorizationPayload): string => {
  const start = formatDate(payload.period_start, CARD_DATE_FORMAT);
  const end = formatDate(payload.period_end, CARD_DATE_FORMAT);
  if (!start && !end) {
    return "";
  }
  return `${start} - ${end}`;
};

/**
 * Maps CPT/HCPCS codes onto service-code card props.
 *
 * @param payload - One authorization body
 * @returns One card per code
 */
const toServiceCodes = (
  payload: CandidAuthorizationPayload,
): ServiceCardInfoCardProps[] => payload.cpt_codes.map((code) => ({ code }));

/**
 * Maps one authorization body onto {@link AuthorizationCardInfoProps}.
 *
 * @param record - Parent write-back row (status and `created_at`)
 * @param payload - One entry from the row's authorization payload array
 * @param index - Zero-based position within the date tab
 * @returns Card props for the Auth Letter grid
 */
export const mapAuthorizationPayloadToCard = (
  record: CandidWritebackRecord,
  payload: CandidAuthorizationPayload,
  index: number,
): AuthorizationCardInfoProps => ({
  header: {
    title: `Authorization #${index + 1}`,
    status: capitalizeString(record.status) || "",
    date: formatDate(record.created_at, CARD_DATETIME_FORMAT),
    ...(record.status === "error" ? ERROR_TAG_COLORS : SUCCESS_TAG_COLORS),
  },
  details: [
    {
      label: "Authorization Number",
      value: payload.authorization_number || "",
    },
    { label: "Payer Name", value: payload.payer_name || "" },
    { label: "Date Range", value: formatPeriodRange(payload) },
    { label: "Auth Notes", value: payload.notes || "" },
    {
      label: "Quantity",
      value: payload.quantity != null ? String(payload.quantity) : "",
    },
    { label: "Units", value: payload.units || "" },
    { label: "DX codes", value: payload.dx_codes.join(", ") || "" },
    {
      label: "Billing Provider",
      value: isValidString(payload.billing_provider_npi)
        ? payload.billing_provider_npi
        : "-",
    },
  ],
  serviceCodes: toServiceCodes(payload),
});
